import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Group, Loader, Text, Title, Table,
  ScrollArea, Paper, Stack, Badge, NumberInput,
  SimpleGrid, ActionIcon, Grid, Modal, Textarea, SegmentedControl, Tooltip
} from '@mantine/core';
import { IconArrowLeft, IconX, IconCurrencyDollar } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../../hooks/useAuthenticatedPost';
import { notifications } from '@mantine/notifications';
import config from '../../../config';

const BASE = config.apiBaseUrl;

const PRICE_AGE_LIMIT = { A: 30, B: 90 };
const PRICE_AGE_TIP = `Price freshness limit: Class A ≤${PRICE_AGE_LIMIT.A} days, Class B ≤${PRICE_AGE_LIMIT.B} days`;
function priceAgeStale(days, cls) {
  if (days == null || cls == null) return false;
  const limit = PRICE_AGE_LIMIT[cls.toUpperCase()];
  return limit != null && days > limit;
}

function fmtDate(dt) {
  return dt ? new Date(dt).toLocaleDateString() : '—';
}
function fmtDateTime(dt) {
  return dt ? new Date(dt).toLocaleString() : '—';
}
function fmtMoney(v) {
  return (v != null && v !== 0) ? `$${Number(v).toLocaleString()}` : '—';
}
function isAfter(dt, soDate) {
  if (!dt || !soDate) return false;
  return new Date(dt) > new Date(soDate);
}

function StatBox({ label, value, color, maxWidth }) {
  const text = value ?? '—';
  return (
    <Box style={maxWidth ? { maxWidth } : undefined}>
      <Text size="xs" c="dimmed" lh={1.2}>{label}</Text>
      <Tooltip label={text} disabled={!maxWidth} withArrow position="bottom">
        <Text size="sm" fw={500} c={color}
          style={maxWidth ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : undefined}>
          {text}
        </Text>
      </Tooltip>
    </Box>
  );
}

function SspRow({ label, priceAtSoDate, price, pricedDate, daysSince }) {
  const stale = daysSince != null && daysSince > 365;
  const changed = priceAtSoDate != null && price != null && priceAtSoDate !== price;
  return (
    <Table.Tr>
      <Table.Td style={{ width: 36, paddingRight: 4 }}><Text size="sm" fw={500}>{label}</Text></Table.Td>
      <Table.Td style={{ textAlign: 'right', paddingRight: 8 }}><Text size="sm" c={changed ? 'orange' : undefined}>{fmtMoney(priceAtSoDate)}</Text></Table.Td>
      <Table.Td style={{ textAlign: 'right', paddingRight: 8 }}><Text size="sm">{fmtMoney(price)}</Text></Table.Td>
      <Table.Td><Text size="xs" c={stale ? 'orange' : 'dimmed'}>{pricedDate ? fmtDate(pricedDate) : '—'}{daysSince != null ? ` (${daysSince}d)` : ''}</Text></Table.Td>
    </Table.Tr>
  );
}

function CompactTable({ headers, rows, emptyMsg, maxH, loading }) {
  return (
    <ScrollArea h={maxH ?? 260}>
      {loading
        ? <Box py="md" style={{ display: 'flex', justifyContent: 'center' }}><Loader size="sm" /></Box>
        : <Table verticalSpacing={3} horizontalSpacing="xs" style={{ fontSize: 12 }} stickyHeader highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                {headers.map(h => (
                  <Table.Th key={h.key} style={{ whiteSpace: 'nowrap', textAlign: h.right ? 'right' : undefined }}>
                    {h.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.length === 0
                ? <Table.Tr><Table.Td colSpan={headers.length}><Text c="dimmed" ta="center" py="sm">{emptyMsg}</Text></Table.Td></Table.Tr>
                : rows}
            </Table.Tbody>
          </Table>}
    </ScrollArea>
  );
}

function MinSellCard({ docNo, line, invoicePrice, salespersonCcode, history, onRefetch, dos, condSsp }) {
  const { post } = useAuthenticatedPost();
  const [val, setVal] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const currentOverride = Array.isArray(history) ? history.find(r => r.isValid) : null;

  useEffect(() => {
    setVal(currentOverride?.minSellPrice != null ? String(currentOverride.minSellPrice) : '');
    setNotes(currentOverride?.notes ?? '');
  }, [currentOverride?.minSellPrice, currentOverride?.notes]);

  const commit = useCallback(async (price) => {
    if (price == null && !currentOverride) return;
    setSaving(true);
    try {
      if (price == null) {
        await post(`${BASE}/sales/invoice-overrides/${encodeURIComponent(docNo)}/${line}/revoke`, {});
        notifications.show({ message: 'Min sell price override removed.', color: 'gray', position: 'bottom-right', autoClose: 3000 });
      } else {
        await post(`${BASE}/sales/invoice-overrides`, {
          docNo, line: parseInt(line),
          salespersonCcode: salespersonCcode ?? '',
          minSellPrice: price,
          notes: notes.trim() || null,
        });
          notifications.show({ message: `Min sell price set to ${fmtMoney(price)}.`, color: 'green', position: 'bottom-right', autoClose: 3000 });
      }
      onRefetch();
    } finally {
      setSaving(false);
    }
  }, [docNo, line, salespersonCcode, notes, currentOverride, post, onRefetch]);

  const handleNotesBlur = useCallback(async () => {
    if (!currentOverride) return;
    const trimmed = notes.trim() || null;
    const existing = currentOverride.notes?.trim() || null;
    if (trimmed === existing) return;
    setSaving(true);
    try {
      await post(`${BASE}/sales/invoice-overrides`, {
        docNo, line: parseInt(line),
        salespersonCcode: salespersonCcode ?? '',
        minSellPrice: currentOverride.minSellPrice,
        notes: trimmed,
      });
      notifications.show({ message: 'Notes saved.', color: 'green', position: 'bottom-right', autoClose: 3000 });
      onRefetch();
    } finally {
      setSaving(false);
    }
  }, [docNo, line, salespersonCcode, notes, currentOverride, post, onRefetch]);

  const handleBlur = () => {
    const parsed = val.trim() === '' ? null : parseFloat(val);
    const current = currentOverride?.minSellPrice ?? null;
    if (parsed === current) return;
    commit(parsed);
  };

  const strategy = getDosStrategy(dos, condSsp);
  const stratColor = strategy == null ? 'dimmed'
    : strategy.pct === 0   ? 'green'
    : strategy.pct <= 5    ? 'teal'
    : strategy.pct <= 10   ? 'yellow'
    : strategy.floor != null ? 'orange'
    : 'red';

  return (
    <Paper withBorder p="md" h="100%">
      <Text fw={500} mb="xs">Min Sell Price</Text>
      {currentOverride
        ? <Text size="xs" c="dimmed" mb="xs">Current: {fmtMoney(currentOverride.minSellPrice)} · {currentOverride.addedByUser} · {fmtDate(currentOverride.dateAdded)}</Text>
        : <Text size="xs" c="dimmed" mb="xs">No override set</Text>}

      {/* DOS strategy narrative */}
      {strategy && (
        <Box mb="xs" p="xs" style={{ borderRadius: 4, border: '1px solid var(--mantine-color-default-border)' }}>
          <Group gap={6} mb={4} wrap="nowrap">
            <Badge size="xs" color={stratColor} variant="light">{strategy.label}</Badge>
            <Text size="xs" c="dimmed">{dos?.toLocaleString()} days on shelf · {strategy.bracket}</Text>
          </Group>
          {strategy.floor != null ? (
            <>
              <Text size="xs" c="dimmed" mb={4}>
                {fmtMoney(condSsp)} × {100 - strategy.pct}% = <Text span fw={600} c={stratColor}>{fmtMoney(strategy.floor)}</Text>
                {strategy.pct === 0 ? ' (hold to SSP)' : ` (max ${strategy.pct}% discount)`}
              </Text>
              <Button
                size="xs" variant="light" color={stratColor} fullWidth
                disabled={saving}
                onClick={() => { setVal(String(strategy.floor)); commit(strategy.floor); }}
              >
                Use strategy — {fmtMoney(strategy.floor)}
              </Button>
            </>
          ) : (
            <Text size="xs" c="red" fw={500}>271+ days — escalate to management before setting price.</Text>
          )}
        </Box>
      )}

      <Group gap="xs" wrap="nowrap" mb="xs">
        <NumberInput
          size="xs"
          style={{ width: 110 }}
          placeholder="Amount"
          value={val === '' ? '' : Number(val)}
          onChange={v => setVal(v === undefined || v === '' ? '' : String(v))}
          onBlur={handleBlur}
          decimalScale={2}
          allowDecimal
          hideControls
          disabled={saving}
        />
        <Tooltip label="Set min sell price to the invoice price" withArrow position="top">
          <ActionIcon
            size="sm" variant="subtle"
            disabled={invoicePrice == null || saving}
            onClick={() => { setVal(String(invoicePrice)); commit(invoicePrice); }}
          >
            <IconCurrencyDollar size={14} />
          </ActionIcon>
        </Tooltip>
        {saving && <Loader size="xs" />}
      </Group>
      <Textarea
        size="xs"
        placeholder="Notes (optional)"
        value={notes}
        onChange={e => setNotes(e.currentTarget.value)}
        onBlur={handleNotesBlur}
        minRows={2}
        autosize
        maxRows={5}
        disabled={saving}
        mb={4}
      />
      <Text size="xs" c="dimmed">Clear the price field and click away to remove override.</Text>
    </Paper>
  );
}

const FUTURE_STYLE = { color: 'rgba(128,128,128,0.55)' };

function getDosStrategy(dos, ssp) {
  if (dos == null || ssp == null || ssp === 0) return null;
  if (dos <= 60)  return { bracket: '0–60 days',    label: 'Hold to SSP',           pct: 0,    floor: ssp };
  if (dos <= 120) return { bracket: '61–120 days',  label: 'Patience',               pct: 5,    floor: Math.round(ssp * 0.95 * 100) / 100 };
  if (dos <= 180) return { bracket: '121–180 days', label: 'Deeper Look',            pct: 10,   floor: Math.round(ssp * 0.90 * 100) / 100 };
  if (dos <= 270) return { bracket: '181–270 days', label: 'Last Chance',            pct: 12,   floor: Math.round(ssp * 0.88 * 100) / 100 };
  return               { bracket: '271+ days',    label: 'Escalate to Management', pct: null, floor: null };
}

export default function InvoiceDetail() {
  const { docNo, line } = useParams();
  const navigate = useNavigate();


  const factsUrl = `${BASE}/sales/invoice-detail/${encodeURIComponent(docNo)}/${line}/facts`;
  const { data: facts, isLoading: factsLoading } = useAuthenticatedData(factsUrl);

  const pn    = facts?.partnumber;
  const logNo = facts?.logNo;
  const soDate = facts?.soDocumentDate;

  const enc = encodeURIComponent(docNo);
  const partUrl    = pn    ? `${BASE}/sales/invoice-detail/${enc}/${line}/part-facts`             : null;
  const salesUrl   = pn    ? `${BASE}/sales/invoice-detail/${enc}/${line}/sales-history`          : null;
  const quoteUrl   = pn    ? `${BASE}/sales/invoice-detail/${enc}/${line}/quote-history?allyears=1` : null;
  const repairUrl  = pn    ? `${BASE}/sales/invoice-detail/${enc}/${line}/repair-history`          : null;
  const invUrl     = pn    ? `${BASE}/sales/invoice-detail/${enc}/${line}/inventory`              : null;
  const pohUrl     = pn    ? `${BASE}/sales/invoice-detail/${enc}/${line}/price-override-history` : null;
  const pricingUrl = pn    ? `${BASE}/sales/invoice-detail/${enc}/${line}/pricing-history`        : null;
  const histUrl    = `${BASE}/sales/invoice-overrides/${enc}/${line}/history`;

  const { data: partFacts }                                       = useAuthenticatedData(partUrl);
  const { data: pricingHistory, isLoading: pricingLoading }      = useAuthenticatedData(pricingUrl);
  const { data: salesHistory,   isLoading: salesLoading }        = useAuthenticatedData(salesUrl);
  const { data: quoteHistory,   isLoading: quoteLoading }   = useAuthenticatedData(quoteUrl);
  const { data: repairHistory,  isLoading: repairLoading }  = useAuthenticatedData(repairUrl);
  const { data: inventory,      isLoading: invLoading }     = useAuthenticatedData(invUrl);
  const { data: priceOvHistory, isLoading: pohLoading, refetch: refetchPoh } = useAuthenticatedData(pohUrl);
  const { data: overrideHistory, refetch: refetchHistory }  = useAuthenticatedData(histUrl);

  const [selectedOverride, setSelectedOverride] = useState(null);
  const [condFilter, setCondFilter] = useState('All');

  if (factsLoading) return <Box p="xl"><Loader /></Box>;
  if (!facts) return <Box p="xl"><Text c="dimmed">Invoice line not found.</Text></Box>;

  const pd = partFacts ?? {};
  const ilsDaysAgo = pd.ilsDate ? Math.round((Date.now() - new Date(pd.ilsDate)) / 86400000) : null;

  const currentOverride = Array.isArray(overrideHistory) ? overrideHistory.find(r => r.isValid) : null;
  // Use SSP at SO date (not current SSP) for all performance comparisons.
  const condSspAtSoMap  = { AR: facts.arSellPriceAtSoDate, SV: facts.svSellPriceAtSoDate, OH: facts.ohSellPriceAtSoDate, NE: facts.newSellPriceAtSoDate, NS: facts.newSellPriceAtSoDate };
  const condSspNowMap   = { AR: pd.arSellPrice, SV: pd.svSellPrice, OH: pd.ohSellPrice, NE: pd.newSellPrice, NS: pd.newSellPrice };
  const condKey         = facts.condition?.toUpperCase();
  const condSsp         = condSspAtSoMap[condKey] ?? null;
  const condSspNow      = condSspNowMap[condKey] ?? null;
  const effSsp          = (currentOverride?.minSellPrice ?? null) != null && currentOverride.minSellPrice !== 0
                            ? currentOverride.minSellPrice
                            : (facts.llp != null && facts.llp !== 0 ? facts.llp : condSsp);
  const effSspNow       = (currentOverride?.minSellPrice ?? null) != null && currentOverride.minSellPrice !== 0
                            ? currentOverride.minSellPrice
                            : (facts.llp != null && facts.llp !== 0 ? facts.llp : condSspNow);
  const effDollar = facts.unitPrice != null && effSsp != null ? (facts.unitPrice - effSsp) * (facts.qty ?? 1) : null;
  const effPct    = facts.unitPrice != null && effSsp != null && effSsp !== 0
                     ? ((facts.unitPrice - effSsp) / effSsp) * 100 : null;
  const condSspChanged = condSsp != null && condSspNow != null && condSsp !== condSspNow;
  const effSspChanged = effSsp != null && effSspNow != null && effSsp !== effSspNow;

  const COND_DATA = ['All', 'AR', 'SV', 'OH'];
  const condCtrl  = <SegmentedControl size="xs" value={condFilter} onChange={setCondFilter} data={COND_DATA} />;
  const byCond    = (arr, key = 'condition') =>
    (Array.isArray(arr) ? arr : []).filter(r => condFilter === 'All' || r[key]?.toUpperCase() === condFilter);

  const filteredPricing = byCond(pricingHistory);
  const filteredSales   = byCond(salesHistory);
  const filteredQuotes  = byCond(quoteHistory);
  const filteredInv     = byCond(inventory);
  const filteredRepairs = byCond(repairHistory, 'expectedRepairedCondition');

  return (
    <Box p="xl">
      {/* Header */}
      <Group mb="md" gap="xs" justify="space-between" align="flex-start">
        <Group gap="xs">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)}>Back</Button>
          <Box>
            <Title order={3}>
              P/N: {facts.partnumber}{pd.description ? ` — ${pd.description}` : ''}
            </Title>
            <Text size="sm" c="dimmed">S/N: {facts.logNo ?? `Line ${line}`}</Text>
          </Box>
        </Group>
        <Box ta="right">
          <Text size="xs" c="dimmed">Pentagon Invoice Doc No:</Text>
          <Text size="sm" fw={600}>{docNo}</Text>
          <Text size="xs" c="dimmed">Line {facts?.userLine ?? line}</Text>
        </Box>
      </Group>

      {/* Invoice fact strip */}
      <Paper withBorder p="sm" mb="md">
        <Group gap="xl" wrap="wrap">
          <StatBox label="PN"           value={facts.partnumber} />
          <StatBox label="Serial"       value={facts.logNo} />
          <StatBox label="Cond"         value={facts.condition?.toUpperCase()} />
          <StatBox label="DOS"          value={facts.dos != null ? facts.dos.toLocaleString() : '—'} />
          <StatBox label="Tag Age"      value={facts.tagAgeSale != null ? facts.tagAgeSale.toLocaleString() : '—'} />
          <Tooltip label={PRICE_AGE_TIP} withArrow position="bottom">
            <Box>
              <StatBox
                label="Price Age"
                value={facts.priceAge != null ? `${facts.priceAge.toLocaleString()}d` : '—'}
                color={priceAgeStale(facts.priceAge, facts.class) ? 'red' : undefined}
              />
            </Box>
          </Tooltip>
          <StatBox label="Key" value={facts.isKeyAccount ? 'Y' : 'N'} />
          <StatBox label="Class"        value={facts.class} />
          <StatBox label="Customer"     value={facts.customerName} maxWidth={160} />
          <StatBox label="Salesperson"  value={facts.salesperson} maxWidth={120} />
          <StatBox label="Phys WH"      value={facts.physicalWarehouseCode} />
          <StatBox label="Virt WH"      value={facts.virtualWarehouseCode} />
          <StatBox label="SO Date"      value={fmtDate(facts.soDocumentDate)} />
          <StatBox label="Invoice Date" value={fmtDate(facts.addedOnDate)} />
          <StatBox label="Invoice Price" value={fmtMoney(facts.unitPrice)} />
          <StatBox label="Pentagon Min" value={fmtMoney(facts.llp)} />
          <Tooltip label={`Current ${condKey} SSP: ${fmtMoney(condSspNow)}`} disabled={!condSspChanged} withArrow position="bottom">
            <Box><StatBox label={`${condKey ?? '?'} SSP @ SO`} value={fmtMoney(condSsp)} color={condSspChanged ? 'orange' : undefined} /></Box>
          </Tooltip>
          <StatBox label="Min Price Adj" value={fmtMoney(currentOverride?.minSellPrice)} />
          <Tooltip label={`Current Eff SSP: ${fmtMoney(effSspNow)} (override → Pentagon min → SSP @ SO)`} disabled={!effSspChanged} withArrow position="bottom">
            <Box><StatBox label="Eff SSP" value={fmtMoney(effSsp)} color={effSspChanged ? 'orange' : undefined} /></Box>
          </Tooltip>
          <StatBox label="$ SvSP"   value={effDollar != null ? `$${effDollar.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'} />
          <StatBox label="% SvSP"   value={effPct != null ? `${effPct.toFixed(1)}%` : '—'} />
        </Group>
      </Paper>

      {/* Four-panel row: SSP | Pricing History | ILS | Min Sell */}
      <SimpleGrid cols={4} mb="md" spacing="md">
        <Paper withBorder p="md">
          <Text fw={500} mb="xs">SSP by Condition</Text>
          {!partFacts ? <Loader size="xs" /> : (
            <Table verticalSpacing={4} horizontalSpacing="xs" style={{ fontSize: 12 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th />
                  <Table.Th style={{ textAlign: 'right', paddingRight: 8 }}><Text size="xs" c="dimmed">At SO Date</Text></Table.Th>
                  <Table.Th style={{ textAlign: 'right', paddingRight: 8 }}><Text size="xs" c="dimmed">Current</Text></Table.Th>
                  <Table.Th><Text size="xs" c="dimmed">Updated</Text></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <SspRow label="AR"  priceAtSoDate={facts.arSellPriceAtSoDate}  price={pd.arSellPrice}  pricedDate={pd.arPricedDate}  daysSince={pd.daysSinceArPricedDate} />
                <SspRow label="SV"  priceAtSoDate={facts.svSellPriceAtSoDate}  price={pd.svSellPrice}  pricedDate={pd.svPricedDate}  daysSince={pd.daysSinceSvPricedDate} />
                <SspRow label="OH"  priceAtSoDate={facts.ohSellPriceAtSoDate}  price={pd.ohSellPrice}  pricedDate={pd.ohPricedDate}  daysSince={pd.daysSinceOhPricedDate} />
                <SspRow label="New" priceAtSoDate={facts.newSellPriceAtSoDate} price={pd.newSellPrice} pricedDate={pd.newPricedDate} daysSince={pd.daysSinceNewPricedDate} />
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        <Paper withBorder p="md">
          <Group justify="space-between" mb="xs" wrap="nowrap">
            <Text fw={500}>Pricing History{Array.isArray(pricingHistory) ? ` (${filteredPricing.length})` : ''}</Text>
            {condCtrl}
          </Group>
          {pricingLoading
            ? <Box py="sm" style={{ display: 'flex', justifyContent: 'center' }}><Loader size="xs" /></Box>
            : <ScrollArea h={180}>
                <Table verticalSpacing={3} horizontalSpacing="xs" style={{ fontSize: 12 }} stickyHeader>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Cond</Table.Th>
                      <Table.Th>From</Table.Th>
                      <Table.Th>To</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Price</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Old Price Age</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredPricing.length === 0
                      ? <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" py="sm">No pricing history found.</Text></Table.Td></Table.Tr>
                      : filteredPricing.map((r, i) => (
                          <Table.Tr key={i}>
                            <Table.Td>{r.condition}</Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.validFrom)}</Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.validTo ? fmtDate(r.validTo) : '—'}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{fmtMoney(r.price)}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{r.priceAge != null ? `${r.priceAge.toLocaleString()}d` : '—'}</Table.Td>
                          </Table.Tr>
                        ))
                    }
                  </Table.Tbody>
                </Table>
              </ScrollArea>
          }
        </Paper>

        <Paper withBorder p="md">
          <Group justify="space-between" mb="xs">
            <Text fw={500}>ILS Market Data</Text>
            <Text size="xs" c="dimmed">{pd.ilsDate ? `${fmtDate(pd.ilsDate)}${ilsDaysAgo != null ? ` (${ilsDaysAgo}d ago)` : ''}` : '—'}</Text>
          </Group>
          {!partFacts ? <Loader size="xs" /> : (
            <SimpleGrid cols={2} spacing="xs">
              <StatBox label="EUI"         value={pd.ilsEndUserInquiry ?? '—'} />
              <StatBox label="Total"       value={pd.ilsTotalInquiries ?? '—'} />
              <StatBox label="Non-EUI"     value={pd.ilsNonEndUserInquiry ?? '—'} />
              <StatBox label="Avg Sources" value={pd.ilsAverageSources != null ? pd.ilsAverageSources.toFixed(1) : '—'} />
            </SimpleGrid>
          )}
        </Paper>

        <MinSellCard
          docNo={docNo}
          line={line}
          invoicePrice={facts.unitPrice}
          salespersonCcode={facts.salespersonCcode}
          history={overrideHistory}
          onRefetch={() => { refetchHistory(); if (refetchPoh) refetchPoh(); }}
          dos={facts.dos}
          condSsp={condSsp}
        />
      </SimpleGrid>

      {/* 2×2 quad grid */}
      <Grid mb="md" gutter="md">
        {/* Sales History */}
        <Grid.Col span={6}>
          <Paper withBorder p="xs">
            <Group justify="space-between" mb="xs" px="xs" wrap="nowrap">
              <Text fw={500}>Sales History{Array.isArray(salesHistory) ? ` (${filteredSales.length})` : ''}</Text>
              {condCtrl}
            </Group>
            <CompactTable
              loading={salesLoading}
              emptyMsg="No sales history found."
              headers={[
                { key: 'date', label: 'SO Date' },
                { key: 'docno', label: 'Doc No' },
                { key: 'customer', label: 'Customer' },
                { key: 'key', label: 'Key' },
                { key: 'cond', label: 'Cond' },
                { key: 'qty', label: 'Qty', right: true },
                { key: 'unit', label: 'Unit $', right: true },
                { key: 'ssp', label: 'SSP', right: true },
                { key: 'pxage', label: 'Price Age', right: true },
                { key: 'dos', label: 'DOS', right: true },
                { key: 'tagage', label: 'Tag Age', right: true },
                { key: 'tag', label: 'Tag' },
                { key: 'logno', label: 'Serial' },
                { key: 'vwh', label: 'Lot WH' },
              ]}
              rows={filteredSales.map((r, i) => {
                const future = isAfter(r.addedOnDate, soDate);
                return (
                  <Table.Tr key={i} style={{ ...(future ? FUTURE_STYLE : {}), fontWeight: r.logNo === logNo ? 600 : undefined }}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.addedOnDate)}</Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.docNo}</Table.Td>
                    <Table.Td style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.accountName ?? ''}>{r.accountName}</Table.Td>
                    <Table.Td>{r.isKeyAccount ? <Badge size="xs" color="teal" variant="filled">Y</Badge> : null}</Table.Td>
                    <Table.Td>{r.condition?.toUpperCase()}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{r.qty}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmtMoney(r.unitPrice)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmtMoney(r.sspAtTime)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Tooltip label={PRICE_AGE_TIP} withArrow position="top">
                        <Text size="sm" c={priceAgeStale(r.priceAge, facts.class) ? 'red' : undefined} fw={priceAgeStale(r.priceAge, facts.class) ? 600 : undefined}>
                          {r.priceAge != null ? r.priceAge.toLocaleString() : '—'}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{r.dos != null ? r.dos.toLocaleString() : ''}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{r.latestTagDate && r.addedOnDate ? Math.round((new Date(r.addedOnDate) - new Date(r.latestTagDate)) / 86400000) : ''}</Table.Td>
                    <Table.Td style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.tag ?? ''}>{r.tag ?? ''}</Table.Td>
                    <Table.Td>{r.logNo}</Table.Td>
                    <Table.Td>{r.virtualWarehouseCode}</Table.Td>
                  </Table.Tr>
                );
              })}
            />
          </Paper>
        </Grid.Col>

        {/* Quote History */}
        <Grid.Col span={6}>
          <Paper withBorder p="xs">
            <Group justify="space-between" mb="xs" px="xs" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Text fw={500}>
                  Quote History{Array.isArray(quoteHistory) ? ` (${filteredQuotes.length})` : ''}
                </Text>
              </Group>
              {condCtrl}
            </Group>
            <CompactTable
              loading={quoteLoading}
              emptyMsg="No quote history found."
              headers={[
                { key: 'date', label: 'Date' },
                { key: 'docno', label: 'Doc No' },
                { key: 'customer', label: 'Customer' },
                { key: 'key', label: 'Key' },
                { key: 'cond', label: 'Cond' },
                { key: 'qty', label: 'Qty', right: true },
                { key: 'unit', label: 'Unit $', right: true },
                { key: 'ssp', label: 'SSP', right: true },
                { key: 'pxage', label: 'Price Age', right: true },
                { key: 'conv', label: 'Conv' },
              ]}
              rows={filteredQuotes.map((r, i) => {
                const future = isAfter(r.quotedDate, soDate);
                return (
                  <Table.Tr key={i} style={future ? FUTURE_STYLE : undefined}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.quotedDate)}</Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.docNo}</Table.Td>
                    <Table.Td style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.accountName ?? ''}>{r.accountName}</Table.Td>
                    <Table.Td>{r.isKeyAccount ? <Badge size="xs" color="teal" variant="filled">Y</Badge> : null}</Table.Td>
                    <Table.Td>{r.condition?.toUpperCase()}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{r.qty}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmtMoney(r.unitPrice)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmtMoney(r.sspAtTime)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Tooltip label={PRICE_AGE_TIP} withArrow position="top">
                        <Text size="sm" c={priceAgeStale(r.priceAge, facts.class) ? 'red' : undefined} fw={priceAgeStale(r.priceAge, facts.class) ? 600 : undefined}>
                          {r.priceAge != null ? r.priceAge.toLocaleString() : '—'}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>{r.converted ? '✓' : ''}</Table.Td>
                  </Table.Tr>
                );
              })}
            />
          </Paper>
        </Grid.Col>

        {/* On-Hand Inventory */}
        <Grid.Col span={6}>
          <Paper withBorder p="xs">
            <Group justify="space-between" mb="xs" px="xs" wrap="nowrap">
              <Text fw={500}>On-Hand Inventory{Array.isArray(inventory) ? ` (${filteredInv.length})` : ''}</Text>
              {condCtrl}
            </Group>
            <CompactTable
              loading={invLoading}
              emptyMsg="No on-hand inventory found."
              headers={[
                { key: 'logno', label: 'Serial' },
                { key: 'cond', label: 'Cond' },
                { key: 'pwh', label: 'Phys WH' },
                { key: 'vwh', label: 'Lot WH' },
                { key: 'loc', label: 'Location' },
                { key: 'received', label: 'Received' },
                { key: 'dos', label: 'DOS', right: true },
                { key: 'tagdate', label: 'Tag Date' },
                { key: 'tagage', label: 'Tag Age', right: true },
                { key: 'tag', label: 'Tag' },
                { key: 'llp', label: 'Min. Price', right: true },
                { key: 'cost', label: 'Cost', right: true },
              ]}
              rows={filteredInv.map((r, i) => {
                const future = isAfter(r.receiverDate, soDate);
                return (
                  <Table.Tr key={i} style={{ ...(future ? FUTURE_STYLE : {}), fontWeight: r.logNo === logNo ? 600 : undefined }}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.logNo}</Table.Td>
                    <Table.Td>{r.condition?.toUpperCase()}</Table.Td>
                    <Table.Td>{r.physicalWarehouseCode}</Table.Td>
                    <Table.Td>{r.virtualWarehouseCode}</Table.Td>
                    <Table.Td>{r.location}</Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.receiverDate)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{r.dos != null ? r.dos.toLocaleString() : ''}</Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.latestTagDate)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{r.latestTagDate ? Math.round((Date.now() - new Date(r.latestTagDate)) / 86400000) : ''}</Table.Td>
                    <Table.Td style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.tag ?? ''}>{r.tag ?? ''}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{(r.llp != null && r.llp !== 0) ? fmtMoney(r.llp) : '—'}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmtMoney(r.cost)}</Table.Td>
                  </Table.Tr>
                );
              })}
            />
          </Paper>
        </Grid.Col>

        {/* Repair History */}
        <Grid.Col span={6}>
          <Paper withBorder p="xs">
            <Group justify="space-between" mb="xs" px="xs" wrap="nowrap">
              <Text fw={500}>Repair History{Array.isArray(repairHistory) ? ` (${filteredRepairs.length})` : ''}</Text>
              {condCtrl}
            </Group>
            <CompactTable
              loading={repairLoading}
              emptyMsg="No repair history for this part."
              headers={[
                { key: 'serial', label: 'Serial' },
                { key: 'entered', label: 'Entered' },
                { key: 'shipped', label: 'Shipped' },
                { key: 'received', label: 'Received' },
                { key: 'tat', label: 'TaT', right: true },
                { key: 'vendor', label: 'Vendor' },
                { key: 'cost', label: 'Cost', right: true },
                { key: 'out', label: 'Out' },
                { key: 'exp', label: 'Exp' },
                { key: 'rcv', label: 'Rcvd' },
                { key: 'ber', label: 'BER' },
              ]}
              rows={filteredRepairs.map((r, i) => (
                <Table.Tr key={i} style={isAfter(r.hdrEnterDate, soDate) ? FUTURE_STYLE : undefined}>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.logNo}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.hdrEnterDate)}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.shippedOn)}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.dateReceived)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{r.taT ?? ''}</Table.Td>
                  <Table.Td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.vendorName ?? ''}>{r.vendorName}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{fmtMoney(r.repairCost)}</Table.Td>
                  <Table.Td>{r.shippedOutCondition?.toUpperCase()}</Table.Td>
                  <Table.Td>{r.expectedRepairedCondition?.toUpperCase()}</Table.Td>
                  <Table.Td>{r.receivedCondition?.toUpperCase()}</Table.Td>
                  <Table.Td>{r.isBer === 'Y' ? 'Yes' : ''}</Table.Td>
                </Table.Tr>
              ))}
            />
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Price Override History — full width below */}
      <Paper withBorder p="xs">
        <Text fw={500} mb="xs" px="xs">
          Price Override History{Array.isArray(priceOvHistory) ? ` (${priceOvHistory.length})` : ''}
        </Text>
        <CompactTable
          maxH={220}
          loading={pohLoading}
          emptyMsg="No price overrides on record for this invoice line."
          headers={[
            { key: 'set', label: 'Date Set' },
            { key: 'price', label: 'Min Sell $', right: true },
            { key: 'by', label: 'Set By' },
            { key: 'from', label: 'Valid From' },
            { key: 'to', label: 'Valid To' },
            { key: 'status', label: 'Status' },
            { key: 'notes', label: 'Notes' },
          ]}
          rows={(Array.isArray(priceOvHistory) ? priceOvHistory : []).map((r, i) => (
            <Table.Tr key={i} style={{ cursor: 'pointer' }} onClick={() => setSelectedOverride(r)}>
              <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.dateAdded)}</Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>{fmtMoney(r.minSellPrice)}</Table.Td>
              <Table.Td>{r.addedByUser}</Table.Td>
              <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.validFrom)}</Table.Td>
              <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.validTo ? fmtDateTime(r.validTo) : '—'}</Table.Td>
              <Table.Td>
                <Badge color={r.isValid ? 'green' : 'gray'} size="xs">
                  {r.isValid ? 'Active' : 'Superseded'}
                </Badge>
              </Table.Td>
              <Table.Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.notes ?? ''}>
                {r.notes ?? ''}
              </Table.Td>
            </Table.Tr>
          ))}
        />
      </Paper>

      {/* Override detail modal */}
      <Modal
        opened={selectedOverride != null}
        onClose={() => setSelectedOverride(null)}
        title={<Text fw={500} style={{ color: 'var(--mantine-color-text)' }}>Price Override Detail</Text>}
        size="md"
      >
        {selectedOverride && (
          <Stack gap="xs">
            <SimpleGrid cols={2} spacing="xs">
              <StatBox label="Min Sell Price" value={fmtMoney(selectedOverride.minSellPrice)} />
              <StatBox label="Status" value={selectedOverride.isValid ? 'Active' : 'Superseded'} />
              <StatBox label="Set By" value={selectedOverride.addedByUser} />
              <StatBox label="Date Set" value={fmtDateTime(selectedOverride.dateAdded)} />
              <StatBox label="Valid From" value={fmtDateTime(selectedOverride.validFrom)} />
              <StatBox label="Valid To" value={selectedOverride.validTo ? fmtDateTime(selectedOverride.validTo) : '—'} />
            </SimpleGrid>
            <Box mt="xs">
              <Text size="xs" c="dimmed" mb={4}>Notes</Text>
              <ScrollArea h={160}>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedOverride.notes?.trim() || <Text size="sm" c="dimmed" fs="italic">No notes.</Text>}
                </Text>
              </ScrollArea>
            </Box>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
